/**
 * Edit at the source: where the Terminology panel sends the attorney for a
 * term, and how the page there arrives at the term's record.
 *
 * The panel never edits a term. It opens the page that defines it with
 * `{ term: { recordId, edit } }` in the router's state, and the page reads
 * that once: it brings the record into view and highlights it, and with
 * `edit` opens it for editing as well.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import type { TermGroupKind } from "@patent-studio/api-types";
import { landOn } from "@/v2/layout/StageFooter";
import { subStepPath, tabPath } from "@/v2/stage-vocabulary";

/** The record a term is defined in, and whether to open it for editing or only show it. */
export interface TermTarget {
  recordId: string;
  edit: boolean;
}

/** How long an arrived-at record stays highlighted, as the design's flash runs. */
export const TERM_FLASH_MS = 2400;

/** The page that defines a group's terms. */
export function termDestination(taskId: string, kind: TermGroupKind): string {
  if (kind === "components") return subStepPath(taskId, "figures", "define");
  if (kind === "elements") return subStepPath(taskId, "thesis", "invention");
  return tabPath(taskId, "thesis", "novelty", "positions");
}

/** The term in a router state, or nothing when the state carries none. */
export function termTargetOf(state: unknown): TermTarget | null {
  if (typeof state !== "object" || state === null || !("term" in state)) {
    return null;
  }
  const { term } = state;
  if (typeof term !== "object" || term === null) return null;
  const { recordId, edit } = term as { recordId?: unknown; edit?: unknown };
  return typeof recordId === "string" && typeof edit === "boolean"
    ? { recordId, edit }
    : null;
}

/**
 * Calls `arrive` once for each visit the panel made, as soon as the page is
 * `ready` to draw the record. A visit is one history entry, so moving within
 * the page does not arrive again, and opening the panel's link again does.
 */
export function useTermArrival(
  ready: boolean,
  arrive: (target: TermTarget) => void,
): void {
  const location = useLocation();
  const arrivedRef = useRef<string | null>(null);
  const target = termTargetOf(location.state);
  useEffect(() => {
    if (!ready || target === null || arrivedRef.current === location.key) {
      return;
    }
    arrivedRef.current = location.key;
    arrive(target);
  });
}

/** Brings the record into view, focused, and highlights it for a moment. */
export function arriveAt(id: string): void {
  landOn(id);
  const record = document.getElementById(id);
  if (record === null) return;
  record.setAttribute("data-term-flash", "");
  window.setTimeout(() => {
    record.removeAttribute("data-term-flash");
  }, TERM_FLASH_MS);
}
